import { Loader } from '@googlemaps/js-api-loader';

// Google Places integration on the new Places API (`AutocompleteSuggestion` + `Place`).
// The legacy `AutocompleteService` / `PlacesService` are disabled on our API key's
// Google Cloud project (legacy "Places API" can't be enabled on projects created after
// 1 March 2025), so `react-google-places-autocomplete` no longer works. This module is
// the drop-in replacement: predictions come back react-select-shaped and details come
// back shaped like the old `PlacesService.getDetails()` result, so every existing
// caller/parser keeps working unchanged.

// The minimal set of new-API Place fields our parsers actually read (mapped back to the
// legacy shape in `toLegacyPlace`). Kept tight so we don't pay for Place Details fields
// nobody consumes.
const DEFAULT_PLACE_FIELDS = [
	'displayName',
	'formattedAddress',
	'addressComponents',
	'location',
	'types',
	'nationalPhoneNumber',
	'regularOpeningHours'
];

let placesLibraryPromise;

// Loads the Google Maps JS `places` library exactly once. The dashboards have no
// `<script>` tag of their own, so `@googlemaps/js-api-loader` injects one with the key.
export const loadPlaces = (apiKey) => {
	if (!placesLibraryPromise) {
		placesLibraryPromise = new Loader({ apiKey }).importLibrary('places');
	}
	return placesLibraryPromise;
};

// new `AutocompleteSuggestion.placePrediction` → a react-select option shaped like the
// `react-google-places-autocomplete` library's, so existing onChange consumers
// (`place.value.place_id`, `place.label`, `place.value.structured_formatting`) are unchanged.
export const toPredictionOption = (placePrediction) => {
	const mainText = placePrediction.mainText?.text ?? placePrediction.text?.text ?? '';
	const fullText = placePrediction.text?.text ?? mainText;
	return {
		label: fullText,
		value: {
			place_id: placePrediction.placeId,
			description: fullText,
			structured_formatting: {
				main_text: mainText,
				secondary_text: placePrediction.secondaryText?.text ?? ''
			}
		}
	};
};

// `establishment` — the only `types` value our call sites pass — is a Table-B collective
// type that the new API rejects in `includedPrimaryTypes`, so we don't narrow (the
// unfiltered suggestions already surface businesses). Genuine Table-A primary types could
// be forwarded here in future.
export const fetchPlacePredictions = async (input, { apiKey } = {}) => {
	if (!input || !input.trim()) return [];
	const { AutocompleteSuggestion } = await loadPlaces(apiKey);
	const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({ input });
	return (suggestions ?? [])
		.map((suggestion) => suggestion.placePrediction)
		.filter(Boolean)
		.map(toPredictionOption);
};

// new `regularOpeningHours` point `{ hour, minute }` → legacy `"HHMM"` string
const toLegacyTime = (point) => `${String(point.hour ?? 0).padStart(2, '0')}${String(point.minute ?? 0).padStart(2, '0')}`;

// new `Place` → an object shaped like a legacy `PlacesService.getDetails()` result, so the
// existing parsers (`parseAddressComponents` / `parseOpeningHours` / the inline HQ +
// retailers logic) keep working without change. `place.location` is a `LatLng` and already
// exposes `.lat()` / `.lng()`, so it slots straight into `geometry.location`.
export const toLegacyPlace = (place) => ({
	place_id: place.id,
	name: place.displayName ?? undefined,
	formatted_address: place.formattedAddress ?? undefined,
	address_components: (place.addressComponents ?? []).map((component) => ({
		long_name: component.longText,
		short_name: component.shortText,
		types: component.types
	})),
	geometry: place.location ? { location: place.location } : undefined,
	formatted_phone_number: place.nationalPhoneNumber ?? undefined,
	opening_hours: place.regularOpeningHours
		? {
				periods: (place.regularOpeningHours.periods ?? []).map((period) => ({
					open: period.open ? { day: period.open.day, time: toLegacyTime(period.open) } : undefined,
					close: period.close ? { day: period.close.day, time: toLegacyTime(period.close) } : undefined
				})),
				weekday_text: place.regularOpeningHours.weekdayDescriptions
			}
		: undefined,
	types: place.types ?? []
});

export const getPlaceDetails = async (placeId, { apiKey, fields } = {}) => {
	const { Place } = await loadPlaces(apiKey);
	const place = new Place({ id: placeId });
	await place.fetchFields({ fields: fields ?? DEFAULT_PLACE_FIELDS });
	return toLegacyPlace(place);
};

// Replaces the legacy `findPlaceFromQuery` (the "search by typed address" path). New Text
// Search returns `Place` objects; we expose the legacy `[{ place_id }]` candidate shape.
export const findPlaceFromQuery = async (query, { apiKey } = {}) => {
	if (!query || !query.trim()) return [];
	const { Place } = await loadPlaces(apiKey);
	const { places } = await Place.searchByText({ textQuery: query, fields: ['id'] });
	return (places ?? []).map((place) => ({ place_id: place.id }));
};

// react-select's AsyncSelect calls `loadOptions` on every keystroke. This collapses a
// burst of calls into a single request and resolves everyone in the burst with the latest
// result — cheaper against the per-request-billed new Autocomplete API.
export const debouncePromise = (fn, wait = 300) => {
	let timer;
	let waiting = [];
	return (...args) =>
		new Promise((resolve, reject) => {
			waiting.push({ resolve, reject });
			clearTimeout(timer);
			timer = setTimeout(() => {
				const batch = waiting;
				waiting = [];
				Promise.resolve()
					.then(() => fn(...args))
					.then(
						(result) => batch.forEach((p) => p.resolve(result)),
						(error) => batch.forEach((p) => p.reject(error))
					);
			}, wait);
		});
};
